import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  applicationDevicesPageQuery,
  applicationDevicesTableQuery,
  type ApplicationDeviceRow,
  type ApplicationDevicesPage,
} from '../api/softsee'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import { escapeHtml } from '../inventoree/format'
import { UpdateAllFromKitForm } from './ApplicationDeviceWrites'

/**
 * Fila Devices a unei aplicatii - softsee/applications/details/devices.php +
 * filter_menu.php, cu `DevicesSoftseeDatatable` din softsee.js: tabelul
 * `#devicesSoftseeList` paginat PE SERVER (`Softsee::devicesSoftseeList`),
 * cautarea, meniul de filtre pe versiune ("<versiune> - N devices",
 * `Softsee::filters`) si butonul "Update All" din antet, care trimite toate
 * masinile la `softsee/updateAllFromKit` cu kitul disponibil.
 *
 * Pana pe 11 septembrie 2026 fila primea TOATE randurile dintr-o data, fara
 * cautare, fara filtru si fara buton, iar coloana "User" scria ultimul
 * utilizator logat in loc de proprietar (auditul Softsee, 1.5). Pe o aplicatie
 * cu 3000 de instalari aducea 3000 de randuri intr-un raspuns.
 *
 * Cele trei coloane sunt `DevicesSoftseeModel`: Device, Owner (cu iconita de
 * utilizator), Version; liniuta cand lipseste. Ordinea de pornire: versiunea,
 * descrescator (`order: [[2, 'desc']]`).
 */

export const TABLE = 'devicesSoftseeList'
const DASH = '<i class="fas fa-minus font-10"></i>'

const COLUMNS: { key: 'device_name' | 'owner_name' | 'version'; title: string; html(row: ApplicationDeviceRow): string }[] = [
  { key: 'device_name', title: 'Device', html: (r) => (r.device_name ? escapeHtml(r.device_name) : DASH) },
  { key: 'owner_name', title: 'Owner', html: (r) => (r.owner_name ? '<i class="fa fa-user"></i> ' + escapeHtml(r.owner_name) : DASH) },
  { key: 'version', title: 'Version', html: (r) => (r.version ? escapeHtml(r.version) : DASH) },
]

export function ApplicationDevicesTab({ id }: { id: number }) {
  const page = useQuery(applicationDevicesPageQuery(id))
  if (page.error) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="alert alert-danger">
            <strong>This did not load.</strong> <span style={{ fontSize: 12 }}>{page.error.message}</span>
          </div>
        </div>
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <Loaded id={id} page={page.data} />
}

function Loaded({ id, page }: { id: number; page: ApplicationDevicesPage }) {
  const [version, setVersion] = useState('')
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 2, dir: 'desc' })
  const column = COLUMNS[order.column] ?? COLUMNS[2]!

  const table = useQuery({
    ...applicationDevicesTableQuery(id, {
      draw: 1,
      start,
      length,
      orderColumn: order.column,
      orderName: column.key,
      orderDir: order.dir,
      search,
      version,
    }),
    placeholderData: keepPreviousData,
  })

  const kit = page.kit
  const canUpdateAll = kit !== null && page.devices.length > 0

  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse" style={{ marginBottom: 10 }}>
        <div className="row">
          <div className="col-md-11">
            <h4>
              <strong>Installed</strong> versions
            </h4>
          </div>
          <div className="col-md-1 right">
            {/* In macheta veche butonul statea dezactivat fara masini sau fara kit
                (`update.prop('disabled', true)`); aici la fel, doar ca formularul
                e cel comun cu fila Update (`UpdateAllFromKitForm`). */}
            {canUpdateAll ? (
              <UpdateAllFromKitForm kit={{ ID: kit.ID, type: kit.type, version: kit.version, name: kit.name }} devices={page.devices} compact />
            ) : (
              <button className="btn btn-xs btn-primary pull-right update-all" type="button" disabled>
                Update All
              </button>
            )}
          </div>
        </div>
      </header>
      <DevicesTable
        rows={table.data?.data ?? []}
        total={table.data?.recordsTotal ?? 0}
        fetching={table.isFetching}
        error={table.isError ? (table.error as Error).message : null}
        versions={page.versions}
        version={version}
        onVersion={(v) => {
          setVersion(v)
          setStart(0)
        }}
        search={search}
        onSearch={(q) => {
          setSearch(q)
          setStart(0)
        }}
        start={start}
        length={length}
        order={order}
        onStart={setStart}
        onLength={(n) => {
          setLength(n)
          setStart(0)
        }}
        onOrder={(next) => {
          setOrder(next)
          setStart(0)
        }}
      />
    </>
  )
}

function DevicesTable({
  rows,
  total,
  fetching,
  error,
  versions,
  version,
  onVersion,
  search,
  onSearch,
  start,
  length,
  order,
  onStart,
  onLength,
  onOrder,
}: {
  rows: ApplicationDeviceRow[]
  total: number
  fetching: boolean
  error: string | null
  versions: ApplicationDevicesPage['versions']
  version: string
  onVersion(v: string): void
  search: string
  onSearch(q: string): void
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  onStart(n: number): void
  onLength(n: number): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const [draft, setDraft] = useState(search)
  const typing = useRef<number | null>(null)

  useEffect(() => {
    setDraft(search)
  }, [search])
  useEffect(() => {
    return () => {
      if (typing.current !== null) window.clearTimeout(typing.current)
    }
  }, [])

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${TABLE}_filter`} className="dataTables_filter input-icon">
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={TABLE}
                value={draft}
                onChange={(e) => {
                  const value = e.target.value
                  setDraft(value)
                  if (typing.current !== null) window.clearTimeout(typing.current)
                  typing.current = window.setTimeout(() => onSearch(value), 400)
                }}
              />
            </label>
          </div>
        </div>
        <div className="col-sm-6 dataTableA"></div>
        <div className="col-sm-12 softseeFilters">
          {/* applications/details/filter_menu.php: meniul apare numai cand sunt versiuni. */}
          {versions.length > 0 ? (
            <dl className="filter-input-list" id="version_filter">
              <dt>Version: </dt>
              <dd>
                <SelectPicker
                  id={`${TABLE}_version`}
                  className="versionFilter"
                  title="Select.."
                  value={version}
                  onChange={onVersion}
                  options={[
                    { value: '', label: 'Select..' },
                    ...versions.map((v) => ({ value: v.version, label: `${v.version} - ${v.number} devices` })),
                  ]}
                />
              </dd>
            </dl>
          ) : null}
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover table-bordered dataTableM dataTable no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => {
                  const sorted = order.column === i
                  const cls = sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting'
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      aria-sort={sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={() => onOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })}
                    >
                      {col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={3} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={3} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {COLUMNS.map((col) => (
                      <td key={col.key} dangerouslySetInnerHTML={{ __html: col.html(row) }} />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
